import { useNavigate } from 'react-router-dom';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import CardProtocol from '../components/CardProtocol';

/**
 * Pasul 4 din 4 al inscrierii: protocolul de colaborare (SP-03).
 *
 * 🔴 E o PREDARE, nu un formular. Ecranul nu cere nimic — da ceva si se da la o
 * parte: fara camp, fara „trimite", fara semnatura IN APLICATIE, si nu blocheaza
 * nimic. Decizia owner-ului.
 *
 * ⚠️ SE-15/SE-16 (owner, 2026-09-16, feedback ECOTIC): procesul s-a schimbat fata de
 * b23 — protocolul chiar are acum circuit de semnatura si retur, doar ca niciunul din
 * ele nu se intampla PE ACEST ECRAN. Reprezentantul legal semneaza pe hartie, iar
 * incarcarea inapoi se face mai tarziu (la inceputul proiectului), din Profil
 * (`NavUser.tsx` — itemul „Incarcare Protocol"; acelasi pas apare acum si in
 * emailul/notificarea de aprobare a scolii, SE-16). Ecranul insusi ramane o predare
 * fara camp — anunta pasul viitor, nu-l executa aici.
 *
 * 🔴 «Descarcă», niciodata «Generează». Verbul care minte nu se vede pe ecran — se
 * vede peste zece minute, cand omul deschide fisierul.
 *
 * ⚠️ MOTIVUL din spatele lui s-a schimbat la b23, si il scriu ca sa nu fie „corectat"
 * inapoi: aici scria ca PDF-ul e al editiei, identic pentru toata lumea. Nu mai e
 * adevarat — `SablonProtocolService.deseneaza()` tipareste numele institutiei, codul
 * SE, editia si data generarii. Documentul E al scolii. Ce ramane generic e SABLONUL:
 * se declara singur «Model orientativ» pe prima pagina si lasa adresa si numele
 * directorului ca spatii de completat pe hartie. Textul de pe cartonas spune fix
 * asta (`CardProtocol`), si a fost gresit pana azi in sens invers.
 *
 * ⚠️ Iesirea proprie NU e decor. `VideoIntroPage` a avut exact bugul asta:
 * butonul se deschidea corect si nu ducea nicaieri, pentru ca ruta il monta fara
 * `onContinua` iar `onContinua?.()` pe `undefined` nu face si nu spune nimic.
 *
 * ⚠️ Niciun text de aici nu spune „s-a salvat in telefon": pe multe browsere de
 * Android un PDF se DESCHIDE intr-un vizualizator in loc sa se salveze. Spunem
 * ce am facut noi, nu ce a facut dispozitivul.
 */

interface Props {
    /** Ce se intampla la «Gata». Injectabil; implicit, pagina de asteptare. */
    onContinua?: () => void;
}

export default function ProtocolPage({ onContinua }: Props) {
    const navigate = useNavigate();
    const { PATHS, PAGES } = useRoutesProvider();
    const caleAsteptare: string = PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER];

    return (
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 pt-20 pb-32 min-h-dvh md:min-h-0 md:overflow-y-auto md:pt-3">
            {/* Centrare pe verticala, ca pagina de asteptare (`page-wrap.jsx`) — dar cu
               scroller PROPRIU: la `md+` utility-ul `ecotic-main-content` are `overflow:
               hidden` si taie tacut tot ce depaseste viewport-ul (fara bara, fara semnal).

               🔴 Centrarea sta pe un COPIL, nu pe scroller. `justify-center` chiar pe
               elementul cu `overflow-y-auto` imparte surplusul de o parte si de alta a
               cutiei, iar partea de sus devine inaccesibila la derulare. Copilul creste
               liber (`min-height: auto`), deci cand continutul e mai inalt decat regiunea
               nu mai exista spatiu liber si centrarea nu taie nimic.

               `pt-20` (hamburgerul flotant) si `pb-32` (bara fixa de jos) stau pe PARINTE,
               in afara cutiei centrate: inauntru s-ar numara ca „continut" si ar impinge
               tot blocul cu jumatate din rezervare.

               🔴 A doua pagina de pas, `/cont-in-asteptare/material-video`, NU se centreaza,
               desi are aceleasi clase de radacina. Masurat pe 2026-09-19, pe build-ul cu
               flagurile din workflow, la 360 si la 1440: acolo centrarea muta titlul cu
               **22 px** intre `se-incarca` si `gata` (starea a doua are in plus bara de
               segmente si legenda, ~44 px), adica rupe cerinta masurata a lui S1 din
               `CadruPasVideo`. Cifra si decizia sunt in jurnal; alinierea celor doua pagini
               se poate relua doar renegociind cerinta, nu prin copierea claselor de aici. */}

            <div className="flex flex-1 flex-col justify-center">
                {/* Pasii, ca in `VideoIntroPage`: al patrulea e cel curent. `pt-20` (pe radacina,
                    de cand pagina e centrata) rezerva locul hamburgerului flotant — motivul si
                    masuratoarea in `CadruPasVideo`. */}
                <div className="flex gap-1.5">
                    <i className="h-1 flex-1 rounded-sm bg-magenta" />
                    <i className="h-1 flex-1 rounded-sm bg-magenta" />
                    <i className="h-1 flex-1 rounded-sm bg-magenta" />
                    <i className="h-1 flex-1 rounded-sm bg-lime-accent" />
                </div>
                <p className="pt-2 text-smaller uppercase tracking-wider text-gray-600 m-0">
                    Pasul 4 din 4
                </p>

                <div className="pt-3">
                    <h1 className="font-heading text-fluid-2xl font-semibold text-dark m-0">
                        Protocolul de colaborare
                    </h1>
                    <p className="mb-4 mt-2.5 text-fluid-base text-dark m-0">
                        Descarcă protocolul și dă-l la semnat reprezentantului legal al școlii.
                        Protocolul semnat va trebui încărcat în platformă la începerea proiectului,
                        din Profil.
                    </p>

                    <CardProtocol variant="flux" />
                </div>
            </div>

            {/* Ancorat jos, ca la pasul de video. Nu depinde de descarcare:
                protocolul nu blocheaza inscrierea, si un buton care ar astepta
                descarcarea ar transforma predarea intr-o poarta. */}

            <div className="fixed inset-x-0 bottom-0 z-10 border-t border-hairline bg-white px-4 pb-4 pt-3 safe-area-bottom">
                <div className="mx-auto w-full max-w-2xl">
                    <Button
                        type="button"
                        onClick={() => {
                            if (onContinua) onContinua();
                            else navigate(caleAsteptare);
                        }}
                        className="min-h-11 w-full border border-input bg-white font-semibold text-dark hover:bg-light"
                    >
                        Gata
                    </Button>
                </div>
            </div>
        </div>
    );
}
